import { useState } from "react";
import { Button, FieldText, Flex, LoadingAnimation, Stack } from "@knowtex-ai/components";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Download } from "iconoir-react";
import { toast } from "react-toastify";
import { getSessionToken } from "../../lib/sessionToken";
import { logInfo } from "../../lib/sentryLogging";

// largeTextSummary.js parses the "PASTED CONTENT:" marker out of this exact
// text to drive map-reduce summarization of huge pastes.
export const SUMMARY_PASTED_CONTENT_PROMPT =
  "Summarize the pasted content below into a clinically organized summary for use as additional context for today's visit note. Group information by category (active problems, relevant history, medications, allergies, recent labs/imaging with dates and values, vitals, and outstanding referrals or follow-ups). Preserve exact medication names, doses, lab values, and dates. Omit administrative text and boilerplate. Output only the requested clinical summary. Do not include a preamble, disclaimer, caveat, explanation, or closing commentary.\n\nPASTED CONTENT:\n";

export const CREATE_INTERVAL_HISTORY_LABEL = "Create Interval History";
const MAKE_CONCISE_LABEL = "Make note concise";
const SUMMARIZE_UPLOADED_FILES_LABEL = "Summarize Uploaded Files";
const REFORMAT_SECTION_ORDER_LABEL = "Reformat to a different section order";
const FIND_REPLACE_LABEL = "Find and replace text";
const SUMMARIZE_PASTED_CONTENT_LABEL = "Summarize Pasted Content";

// Matches the deterministic bracket grammar in server/services/patientChat/literalReplace.js.
// Pre-filling this template steers users into the parseable syntax instead of free-form
// phrasing that would silently fall through to a generative rewrite.
export const FIND_REPLACE_TEMPLATE =
  "replace every instance of [TEXT TO FIND] with [REPLACEMENT TEXT].";

const matchesQuickActionLabel = (value, label) => value === label || value.startsWith(`${label}: `);

// Kept for back-compat with Interval History drafts persisted before this button's
// prompt moved server-side.
const LEGACY_INTERVAL_HISTORY_PROMPT_PREFIX = "Draft only the Interval History for this visit.";

export const isIntervalHistoryPromptText = (value) => {
  const trimmed = String(value || "").trim();
  return (
    matchesQuickActionLabel(trimmed, CREATE_INTERVAL_HISTORY_LABEL) ||
    trimmed.startsWith(LEGACY_INTERVAL_HISTORY_PROMPT_PREFIX)
  );
};

// Reformat/Find & Replace aren't included here: they send the user's raw text
// instead of a label prefix, since the server needs that raw text for order
// embedding and the deterministic find-and-replace parser.
const LABEL_PREFIXED_ORIGIN_LABELS = [
  CREATE_INTERVAL_HISTORY_LABEL,
  MAKE_CONCISE_LABEL,
  SUMMARIZE_UPLOADED_FILES_LABEL,
];

export const isOriginatingSmartAssistPromptText = (value) => {
  const trimmed = String(value || "").trim();
  return (
    LABEL_PREFIXED_ORIGIN_LABELS.some((label) => matchesQuickActionLabel(trimmed, label)) ||
    trimmed.startsWith(SUMMARY_PASTED_CONTENT_PROMPT) ||
    trimmed.startsWith(LEGACY_INTERVAL_HISTORY_PROMPT_PREFIX)
  );
};

// Fallback for stored history without a live quick-action id (e.g. after a page reload).
export const conversationHasIntervalHistoryDraft = (conversation = []) => {
  for (let index = conversation.length - 1; index >= 0; index -= 1) {
    if (conversation[index]?.role !== "user") {
      continue;
    }

    const content = conversation[index].content;
    if (isIntervalHistoryPromptText(content)) {
      return true;
    }
    if (isOriginatingSmartAssistPromptText(content)) {
      return false;
    }
  }
  return false;
};

/** @type {import("../smartAssist/core/types").SmartAssistQuickAction[]} */
export const REVIEW_SMART_ASSIST_PROMPTS = [
  {
    id: "reformat_section_order",
    label: REFORMAT_SECTION_ORDER_LABEL,
    instruction: "Type the section order you want (e.g. SOAP or APSO), then submit.",
    requestKind: "note_rewrite",
    quickAction: "reformat_section_order",
    needsUserInput: true,
  },
  {
    id: "find_replace",
    label: FIND_REPLACE_LABEL,
    instruction: "Replace the bracketed placeholders below with your terms, then submit.",
    prefillText: FIND_REPLACE_TEMPLATE,
    requestKind: "note_rewrite",
    needsUserInput: true,
  },
  {
    id: "make_concise",
    label: MAKE_CONCISE_LABEL,
    instruction: "Tightens the wording without cutting clinical content. No extra input needed - just Submit.",
    requestKind: "note_rewrite",
    quickAction: "make_concise",
    needsUserInput: false,
  },
];

/** @type {import("../smartAssist/core/types").SmartAssistQuickAction[]} */
export const SMART_CHART_SMART_ASSIST_PROMPTS = [
  {
    id: "summarize_pasted_content",
    label: SUMMARIZE_PASTED_CONTENT_LABEL,
    instruction: "Paste the content you'd like summarized below, then submit.",
    needsUserInput: true,
    buildMessage: (userText) => `${SUMMARY_PASTED_CONTENT_PROMPT}${userText}`,
    offersConsentToggle: true,
  },
  {
    id: "summarize_uploaded_files",
    label: SUMMARIZE_UPLOADED_FILES_LABEL,
    instruction: "Attach the files you'd like summarized using the paperclip button below, then submit.",
    requestKind: "objective_data_summary",
    needsUserInput: false,
    requiresAttachment: true,
  },
  {
    id: "create_interval_history",
    label: CREATE_INTERVAL_HISTORY_LABEL,
    instruction:
      "Drafts the Interval History from this note, transcript, patient summary, and chart history. No extra input needed - just Submit.",
    requestKind: "note_rewrite",
    quickAction: "create_interval_history",
    needsUserInput: false,
  },
];

export const PROMPT_WORKFLOW_INSTRUCTION =
  "Paste an example of your perfect note, in the format that you want Knowtex to output future notes into.";

export const EMPTY_SMART_ASSIST_PROMPT_MESSAGE = "Enter a prompt for Smart Assist.";
export const SMART_ASSIST_FILE_LIMIT_MESSAGE = "You can only upload up to 5 files.";

/** @type {{ IDLE: "idle", AWAITING_EXAMPLE: "awaiting_example", CRAFTING: "crafting", COMPLETE: "complete" }} */
export const PROMPT_WORKFLOW_STEPS = {
  IDLE: "idle",
  AWAITING_EXAMPLE: "awaiting_example",
  CRAFTING: "crafting",
  COMPLETE: "complete",
};

export function PromptWorkflowStatus({ step, quickAction }) {
  if (quickAction) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-violet-200 bg-violet-50 px-4 py-4 text-sm leading-6 text-accent1">
        <div className="font-semibold">{quickAction.label}</div>
        <div className="mt-1">{quickAction.instruction}</div>
      </div>
    );
  }

  if (step === PROMPT_WORKFLOW_STEPS.AWAITING_EXAMPLE) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-violet-200 bg-violet-50 px-4 py-4 text-sm leading-6 text-accent1">
        <div className="font-semibold">Create a custom prompt</div>
        <div className="mt-1">{PROMPT_WORKFLOW_INSTRUCTION}</div>
      </div>
    );
  }

  if (step === PROMPT_WORKFLOW_STEPS.CRAFTING) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm font-semibold text-accent1">
        <LoadingAnimation size={32} color="#783BFF" />
        Crafting your prompt…
      </div>
    );
  }

  return null;
}

export function PromptResultTools({ promptText, summary, onMacroSaved }) {
  const [showDotPhraseForm, setShowDotPhraseForm] = useState(false);
  const [dotPhrase, setDotPhrase] = useState("");
  const [dotPhraseError, setDotPhraseError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPromptCopied, setIsPromptCopied] = useState(false);

  const downloadPrompt = () => {
    const blob = new Blob([promptText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "smart-assist-custom-prompt.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const saveDotPhrase = async () => {
    const rawName = dotPhrase.trim();
    if (!rawName) {
      setDotPhraseError("Enter a dot phrase name.");
      return;
    }
    const normalizedName = rawName.startsWith(".") ? rawName : `.${rawName}`;
    if (!/^\.[A-Za-z0-9_-]+$/.test(normalizedName)) {
      setDotPhraseError("Use letters, numbers, hyphens, or underscores after the dot.");
      return;
    }

    setDotPhraseError("");
    setIsSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/macros/new-macro`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: getSessionToken(),
        },
        body: JSON.stringify({
          dot_phrase: normalizedName,
          keywords: [normalizedName.slice(1)],
          content: promptText,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload.error ||
            "The dot phrase could not be saved. Check the name and shorten the prompt, then try again."
        );
      }
      logInfo("Macro created", "macro.created", {
        source: "smart_assist",
        keyword_count: 1,
        content_length: promptText?.length ?? 0,
      });
      setDotPhrase(normalizedName);
      setDotPhraseError("");
      setShowDotPhraseForm(false);
      await onMacroSaved?.();
      toast.success(`${normalizedName} saved to your dot phrase library.`);
    } catch (error) {
      setDotPhraseError(
        error.message ||
          "The dot phrase could not be saved. Check the name and shorten the prompt, then try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50 px-3 py-3 text-sm text-accent1">
      <div className="font-semibold">Your custom prompt is ready.</div>
      <p className="mb-0 mt-1 text-xs leading-5 text-knowtex-grey">
        Copy this prompt and paste it into <strong>Custom Prompt</strong>.
      </p>
      {summary && <p className="mb-0 mt-2 text-xs leading-5 text-knowtex-grey">{summary}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <CopyToClipboard text={promptText} onCopy={() => setIsPromptCopied(true)}>
          <Button type="button" variant="secondary" size="small">
            {isPromptCopied ? <span className="text-success">✓ Copied</span> : "Copy prompt"}
          </Button>
        </CopyToClipboard>
        <Button type="button" variant="secondary" size="small" icon={Download} onClick={downloadPrompt}>
          Download .txt
        </Button>
      </div>

      <div className="mt-3 border-t border-violet-200 pt-3">
        <div className="text-xs font-semibold">Would you like to save this as a dot phrase?</div>
        {!showDotPhraseForm ? (
          <Button
            type="button"
            variant="primary"
            size="small"
            onClick={() => setShowDotPhraseForm(true)}
            className="mt-2"
          >
            Save as dot phrase
          </Button>
        ) : (
          <Stack gap={8} className="mt-2">
            <Flex wrap="wrap" gap={8} alignItems="flex-start">
              <FieldText
                label="Dot phrase name"
                value={dotPhrase}
                onChange={(event) => {
                  setDotPhrase(event.target.value);
                  setDotPhraseError("");
                }}
                placeholder=".mycustomprompt"
                error={dotPhraseError || undefined}
                className="min-w-0 flex-1"
              />
              <Button type="button" variant="primary" size="small" disabled={isSaving} loading={isSaving} onClick={saveDotPhrase}>
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </Flex>
            {dotPhraseError && (
              <span role="alert" className="w-full text-xs font-semibold text-red-600">
                {dotPhraseError}
              </span>
            )}
          </Stack>
        )}
      </div>
    </div>
  );
}
